<template>
  <section class="what-we-offer-section">
    <v-container class="offer-container animate-block">
      <!-- Section Header -->
      <div class="section-header">
        <h2 class="section-title">{{ $t('offer.title') }}</h2>
        <p class="section-subtitle">{{ $t('offer.subtitle') }}</p>
      </div>

      <!-- Content Row -->
      <v-row justify="center" class="content-row">
        <!-- Left - Circle Progress -->
        <v-col cols="12" md="auto" class="logo-col">
          <div class="circle-wrapper">
            <svg class="circle-progress" viewBox="0 0 183 183" xmlns="http://www.w3.org/2000/svg">
              <circle class="circle-bg" cx="91.5" cy="91.5" r="80" />
              <circle
                class="circle-fill"
                cx="91.5"
                cy="91.5"
                r="80"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="dashOffset"
              />
            </svg>
            <img
              src="@/assets/logo.png"
              alt="Invest Broker"
              class="circle-logo"
            />
          </div>
        </v-col>

        <!-- Right - Text -->
        <v-col cols="12" md="5" offset-md="1" class="text-col">
          <transition name="offer-fade" mode="out-in">
            <p :key="currentIndex" class="offer-text">{{ currentPhrase }}</p>
          </transition>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import { animateOnScroll } from '@/mixins/animateOnScroll'

export default {
  name: 'WhatWeOfferSection',
  mixins: [animateOnScroll],
  data() {
    return {
      currentIndex: 0,
      isLocked: false,
      unlockCooldown: false,
      isThrottled: false,
      accumulatedDelta: 0
    }
  },
  computed: {
    currentPhrase() {
      const phrases = this.$tm('offer.phrases')
      return phrases[this.currentIndex] || phrases[0]
    },
    circumference() {
      return 2 * Math.PI * 80
    },
    dashOffset() {
      const phrases = this.$tm('offer.phrases')
      const progress = phrases.length > 1 ? this.currentIndex / (phrases.length - 1) : 0
      return this.circumference * (1 - progress)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('wheel', this.onWheel, { passive: false })
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('wheel', this.onWheel)
    this.forceUnlock()
  },
  methods: {
    lockScroll() {
      if (this.isLocked) return
      const el = this.$el
      if (!el) return
      const rect = el.getBoundingClientRect()
      const diff = (rect.top + rect.height / 2) - (window.innerHeight / 2)
      // targetY — позиция скролла при которой секция точно по центру
      const targetY = Math.round(window.scrollY + diff)
      this._lockY = targetY
      document.documentElement.style.scrollBehavior = 'auto'
      const scrollbarW = window.innerWidth - document.documentElement.clientWidth
      document.body.style.position = 'fixed'
      document.body.style.top = `-${targetY}px`
      document.body.style.width = '100%'
      document.body.style.paddingRight = `${scrollbarW}px`
      this.isLocked = true
    },
    unlockScroll() {
      if (!this.isLocked) return
      this.isLocked = false
      this.isThrottled = false
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.paddingRight = ''
      // scrollTo до восстановления scroll-behavior, иначе App.vue smooth анимирует с 0 до lockY
      window.scrollTo(0, this._lockY)
      setTimeout(() => {
        document.documentElement.style.scrollBehavior = ''
      }, 50)
      this.unlockCooldown = true
      setTimeout(() => { this.unlockCooldown = false }, 1000)
    },
    forceUnlock() {
      this.isLocked = false
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.paddingRight = ''
      document.documentElement.style.scrollBehavior = ''
    },
    onScroll() {
      if (this.isLocked || this.unlockCooldown) return
      const el = this.$el
      if (!el) return
      const rect = el.getBoundingClientRect()
      const diff = (rect.top + rect.height / 2) - (window.innerHeight / 2)
      // порог 20% высоты экрана — достаточно широко чтобы поймать быстрый скролл
      if (Math.abs(diff) < window.innerHeight * 0.2) {
        this.lockScroll()
      }
    },
    onWheel(e) {
      if (!this.isLocked) return
      e.preventDefault()
      if (this.isThrottled) return
      const clamped = Math.max(-120, Math.min(120, e.deltaY))
      this.accumulatedDelta += clamped
      if (Math.abs(this.accumulatedDelta) < 80) return
      const direction = this.accumulatedDelta > 0 ? 1 : -1
      this.accumulatedDelta = 0
      this.isThrottled = true
      setTimeout(() => { this.isThrottled = false }, 650)
      const phrases = this.$tm('offer.phrases')
      if (direction > 0) {
        if (this.currentIndex < phrases.length - 1) {
          this.currentIndex++
        } else {
          this.unlockScroll()
        }
      } else {
        if (this.currentIndex > 0) {
          this.currentIndex--
        } else {
          this.unlockScroll()
        }
      }
    }
  }
}
</script>

<style scoped>
.what-we-offer-section {
  background-color: #ededed;
  padding: 80px 0;
}

.offer-container {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.section-header {
  width: 511px;
  max-width: 100%;
}

.section-title {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 32px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 41.6px;
  color: rgb(28, 165, 190);
  margin: 0;
}

.section-subtitle {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 32px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 41.6px;
  color: rgb(46, 46, 46);
  margin: 0;
}

.content-row {
  justify-content: center;
}

.logo-col {
  display: flex;
  justify-content: center;
}

.circle-wrapper {
  position: relative;
  width: 183px;
  height: 183px;
}

.circle-progress {
  width: 183px;
  height: 183px;
  display: block;
  transform: rotate(-90deg);
}

.circle-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90px;
  height: 90px;
  object-fit: contain;
}

.circle-bg {
  fill: none;
  stroke: #E7E7E7;
  stroke-width: 6;
}

.circle-fill {
  fill: none;
  stroke: rgb(28, 165, 190);
  stroke-width: 6;
  stroke-linecap: butt;
  transition: stroke-dashoffset 0.35s ease;
}

.text-col {
  display: flex;
  align-items: center;
}

.offer-text {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 32px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 41.6px;
  color: rgb(46, 46, 46);
  margin: 0;
}

/* Phrase fade transition */
.offer-fade-enter-active,
.offer-fade-leave-active {
  transition: opacity 0.35s ease;
}

.offer-fade-enter-from,
.offer-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 960px) {
  .what-we-offer-section {
    padding: 60px 0;
  }

  .section-header {
    width: 100%;
  }

  .section-title,
  .section-subtitle {
    font-size: 26px;
    line-height: 34px;
  }

  .circle-wrapper,
  .circle-progress {
    width: 140px;
    height: 140px;
  }

  .circle-logo {
    width: 75px;
    height: 75px;
  }

  .offer-text {
    font-size: 22px;
    line-height: 30px;
    text-align: center;
  }
}

@media (max-width: 600px) {
  .what-we-offer-section {
    padding: 40px 0;
  }

  .section-title,
  .section-subtitle {
    font-size: 20px;
    line-height: 28px;
  }

  .circle-wrapper,
  .circle-progress {
    width: 110px;
    height: 110px;
  }

  .circle-logo {
    width: 60px;
    height: 60px;
  }

  .offer-text {
    font-size: 18px;
    line-height: 26px;
  }
}
</style>
